import cloneDeep from 'lodash/cloneDeep';
import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';

import { SecondarySolidButton, SecondaryTextButton } from '@bibliocommons/base-buttons';
import { SecondaryLink } from '@bibliocommons/base-links';
import TextField from 'app/components/shared/TextField';

import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from 'app/components/shared/Table';
import ToggleSwitch from 'app/components/shared/ToggleSwitch';
import ContextMenu from 'app/components/admin/browse/shared/ContextMenu';
import ReOrderButtons from 'app/components/admin/browse/shared/ReOrderButtons';
import { DEFAULT_CATEGORY_VALIDATION } from 'app/constants/admin/browse/categories';
import { makeCategorySlug, makeEmptyCategory, validateCategory } from 'app/helpers/admin/browse/categories';
import { categoryShape } from 'app/shapes/admin/browse/categories';

import './CategoryListEditor.scss';

const ENTER_KEY_CODE = 13;

const CategoryListEditor = ({
  categories,
  categoryColumnHeading,
  deleteCategory,
  onAddCategory,
  onCategoryChange,
  onReOrder,
  parentCategoryId
}) => {
  const createCategoryTitleRef = useRef(null);

  const [state, setState] = useState({
    createCategory: makeEmptyCategory(),
    createCategoryValidation: DEFAULT_CATEGORY_VALIDATION,
    editingCategory: null,
    editingCategoryValidation: DEFAULT_CATEGORY_VALIDATION
  });

  const { createCategory, createCategoryValidation, editingCategory, editingCategoryValidation } = state;

  const handleCreateCategorySave = () => {
    const newCreateCategoryValidation = validateCategory(createCategory, { siblingCategories: categories });

    if (newCreateCategoryValidation.isValid) {
      onAddCategory(parentCategoryId, createCategory);

      setState(prevState => {
        return {
          ...prevState,
          createCategory: makeEmptyCategory(),
          createCategoryValidation: DEFAULT_CATEGORY_VALIDATION
        };
      });

      if (createCategoryTitleRef.current) {
        createCategoryTitleRef.current.focus();
      }
    } else {
      setState(prevState => {
        return {
          ...prevState,
          createCategoryValidation: newCreateCategoryValidation
        };
      });
    }
  };

  const handleCreateCategorySearchUrlChange = newSearchUrl => {
    setState(prevState => {
      return {
        ...prevState,
        createCategory: {
          ...prevState.createCategory,
          searchUrl: newSearchUrl
        }
      };
    });
  };

  const handleCreateCategorySlugChange = newSlug => {
    setState(prevState => {
      return {
        ...prevState,
        createCategory: {
          ...prevState.createCategory,
          slug: newSlug
        }
      };
    });
  };

  const handleCreateCategoryTitleChange = newTitle => {
    const newSlug = makeCategorySlug(newTitle);

    setState(prevState => {
      return {
        ...prevState,
        createCategory: {
          ...prevState.createCategory,
          slug: newSlug,
          title: newTitle
        }
      };
    });
  };

  const handleCreateCategoryInputKeyPress = event => {
    const keyCode = event.keyCode || event.charCode;
    if (keyCode !== ENTER_KEY_CODE) {
      return;
    }

    event.preventDefault();

    handleCreateCategorySave();
  };

  const handleEditCategoryCancel = () => {
    setState(prevState => {
      return {
        ...prevState,
        editingCategory: null,
        editingCategoryValidation: DEFAULT_CATEGORY_VALIDATION
      };
    });
  };

  const handleEditCategorySave = () => {
    const newEditCategoryValidation = validateCategory(editingCategory, { siblingCategories: categories });

    if (newEditCategoryValidation.isValid) {
      onCategoryChange(editingCategory);

      setState(prevState => {
        return {
          ...prevState,
          editingCategory: null,
          editingCategoryValidation: DEFAULT_CATEGORY_VALIDATION
        };
      });
    } else {
      setState(prevState => {
        return {
          ...prevState,
          editingCategoryValidation: newEditCategoryValidation
        };
      });
    }
  };

  const handleEditSearchUrlInputKeyPress = event => {
    const keyCode = event.keyCode || event.charCode;
    if (keyCode !== ENTER_KEY_CODE) {
      return;
    }

    handleEditCategorySave();
  };

  const handleEditSearchUrlChange = newSearchUrl => {
    setState(prevState => {
      return {
        ...prevState,
        editingCategory: {
          ...prevState.editingCategory,
          searchUrl: newSearchUrl
        }
      };
    });
  };

  const handleEditSlugChange = newSlug => {
    setState(prevState => {
      return {
        ...prevState,
        editingCategory: {
          ...prevState.editingCategory,
          slug: newSlug
        }
      };
    });
  };

  const handleEditSlugInputKeyPress = event => {
    const keyCode = event.keyCode || event.charCode;
    if (keyCode !== ENTER_KEY_CODE) {
      return;
    }

    handleEditCategorySave();
  };

  const handleEditTitleInputKeyPress = event => {
    const keyCode = event.keyCode || event.charCode;
    if (keyCode !== ENTER_KEY_CODE) {
      return;
    }

    handleEditCategorySave();
  };

  const handleEditTitleChange = newTitle => {
    setState(prevState => {
      return {
        ...prevState,
        editingCategory: {
          ...prevState.editingCategory,
          title: newTitle
        }
      };
    });
  };

  const handleEditClick = category => {
    const newEditingCategory = cloneDeep(category);

    setState(prevState => {
      return {
        ...prevState,
        editingCategory: newEditingCategory
      };
    });
  };

  const handleReOrder = newOrderedCategories => {
    onReOrder(
      parentCategoryId,
      newOrderedCategories.map(category => category.categoryId)
    );
  };

  const handleToggleIsEnabled = categoryId => {
    const oldCategory = categories.find(category => category.categoryId === categoryId);
    const newCategory = {
      ...oldCategory,
      isEnabled: !oldCategory.isEnabled
    };

    onCategoryChange(newCategory);
  };

  const keyPrefix = useMemo(() => {
    return uniqueId('CategoryListEditor_');
  }, []);
  const createCategoryRowKey = `${keyPrefix}_CreateCategoryRow`;
  const createCategoryTitleInputId = `${keyPrefix}_CreateCategoryLabel`;
  const createCategorySearchUrlInputId = `${keyPrefix}_CreateCategorySearchUrl`;
  const createCategorySlugInputId = `${keyPrefix}_CreateCategorySlug`;

  const hasCreateSlugError = Boolean(createCategoryValidation.errorByAttribute.slug);

  return (
    <div className="cp-category-list-editor">
      <Table>
        <colgroup>
          <col style={{ width: '25%' }}></col>
          <col style={{ width: '25%' }}></col>
          <col style={{ width: '25%' }}></col>
          <col></col>
          <col style={{ width: '136px' }}></col>
        </colgroup>
        <TableHeader>
          <TableHeading>{categoryColumnHeading}</TableHeading>
          <TableHeading>Catalog search URL</TableHeading>
          <TableHeading>URL slug</TableHeading>
          <TableHeading className="cell-short">Status</TableHeading>
          <TableHeading />
        </TableHeader>
        <TableBody>
          {categories.map((category, index) => {
            const { categoryId, isEnabled, searchUrl, slug, title } = category;

            const confirmDeleteModalMessage = `Are you sure you want to delete the '${title}' category?`;
            const isEditing = editingCategory?.categoryId === categoryId;
            const {
              searchUrl: searchUrlError,
              slug: slugError,
              title: titleError
            } = editingCategoryValidation.errorByAttribute;

            const cancelEditButtonKey = `${categoryId}_CancelEdit`;
            const saveButtonKey = `${categoryId}_Save}`;
            const searchUrlInputId = `${categoryId}_SearchUrl`;
            const slugInputId = `${categoryId}_Slug`;
            const titleInputId = `${categoryId}_Title`;

            return isEditing ? (
              <TableRow key={categoryId}>
                <TableCell className="table-cell-editing">
                  <TextField
                    errorMessage={titleError}
                    hideLabel
                    id={titleInputId}
                    label="Title"
                    onChange={handleEditTitleChange}
                    onKeyPress={handleEditTitleInputKeyPress}
                    type="text"
                    value={editingCategory?.title}
                  />
                </TableCell>
                <TableCell className="table-cell-editing">
                  <TextField
                    className="input-search-url"
                    errorMessage={searchUrlError}
                    hideLabel
                    id={searchUrlInputId}
                    label="Catalog search URL"
                    onChange={handleEditSearchUrlChange}
                    onKeyPress={handleEditSearchUrlInputKeyPress}
                    type="text"
                    value={editingCategory?.searchUrl}
                  />
                </TableCell>
                <TableCell className="table-cell-editing">
                  <div className="input-with-action-wrapper">
                    <TextField
                      errorMessage={slugError}
                      hideLabel
                      id={slugInputId}
                      label="URL Slug"
                      onChange={handleEditSlugChange}
                      onKeyPress={handleEditSlugInputKeyPress}
                      type="text"
                      value={editingCategory?.slug}
                    />
                    <SecondarySolidButton
                      className="action-save-category"
                      dataKey={saveButtonKey}
                      handleClick={handleEditCategorySave}
                      type="button"
                    >
                      Save
                    </SecondarySolidButton>
                  </div>
                </TableCell>
                <TableCell className="table-cell-editing" colSpan={2}>
                  <SecondaryTextButton
                    className="action-cancel"
                    dataKey={cancelEditButtonKey}
                    handleClick={handleEditCategoryCancel}
                    leftAligned
                    type="button"
                  >
                    Cancel
                  </SecondaryTextButton>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={categoryId}>
                <TableCell>
                  <span>{title}</span>
                </TableCell>
                <TableCell>
                  <div className="search-url-wrapper">
                    <div className="search-url">
                      <SecondaryLink dataKey={searchUrl} href={searchUrl} target="_blank">
                        {searchUrl}
                      </SecondaryLink>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="slug">{'/'.concat(slug)}</span>
                </TableCell>
                <TableCell className="cell-short">
                  <div>
                    <ToggleSwitch
                      active={isEnabled}
                      activeText="Active"
                      handleClick={() => handleToggleIsEnabled(categoryId)}
                      id={categoryId}
                      inactiveText="Inactive"
                    />
                  </div>
                </TableCell>
                <TableCell className="cell-short">
                  <div className="cell-actions-inner">
                    <ReOrderButtons index={index} items={categories} onChange={handleReOrder} />
                    <ContextMenu
                      className="action-context-menu"
                      confirmDeleteModalHeading="Delete category"
                      confirmDeleteModalMessage={confirmDeleteModalMessage}
                      onDeleteClick={() => deleteCategory(categoryId)}
                      onEditClick={() => handleEditClick(category)}
                    />
                  </div>
                </TableCell>
              </TableRow>
            );
          })}

          <TableRow key={createCategoryRowKey}>
            <TableCell className="cell-add-category">
              <TextField
                errorMessage={createCategoryValidation.errorByAttribute.title}
                id={createCategoryTitleInputId}
                label="Title"
                onChange={handleCreateCategoryTitleChange}
                onKeyPress={handleCreateCategoryInputKeyPress}
                ref={createCategoryTitleRef}
                type="text"
                value={createCategory.title}
              />
            </TableCell>
            <TableCell className="cell-add-category" colSpan={hasCreateSlugError ? 1 : 2}>
              <div className="input-with-action-wrapper">
                <TextField
                  className="input-search-url"
                  errorMessage={createCategoryValidation.errorByAttribute.searchUrl}
                  id={createCategorySearchUrlInputId}
                  label="Catalog search URL"
                  onChange={handleCreateCategorySearchUrlChange}
                  onKeyPress={handleCreateCategoryInputKeyPress}
                  type="text"
                  value={createCategory.searchUrl}
                />

                {!hasCreateSlugError && (
                  <SecondarySolidButton
                    className="action-create-category"
                    dataKey="create-category-button"
                    handleClick={handleCreateCategorySave}
                    type="button"
                  >
                    Add
                  </SecondarySolidButton>
                )}
              </div>
            </TableCell>
            {hasCreateSlugError && (
              <TableCell className="cell-add-category">
                <div className="input-with-action-wrapper">
                  <TextField
                    errorMessage={createCategoryValidation.errorByAttribute.slug}
                    id={createCategorySlugInputId}
                    label="URL slug"
                    onChange={handleCreateCategorySlugChange}
                    onKeyPress={handleCreateCategoryInputKeyPress}
                    type="text"
                    value={createCategory.slug}
                  />
                  <SecondarySolidButton
                    className="action-create-category"
                    dataKey="create-category-button"
                    handleClick={handleCreateCategorySave}
                    type="button"
                  >
                    Add
                  </SecondarySolidButton>
                </div>
              </TableCell>
            )}
            <TableCell colSpan={2}></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

CategoryListEditor.propTypes = {
  categories: PropTypes.arrayOf(categoryShape).isRequired,
  categoryColumnHeading: PropTypes.string.isRequired,
  deleteCategory: PropTypes.func.isRequired,
  parentCategoryId: PropTypes.string.isRequired,
  onAddCategory: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  onReOrder: PropTypes.func.isRequired
};

export default CategoryListEditor;
